import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { projectStatusLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { addDays, formatDate, formatMoney, localIsoDate } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { RichTextEditor } from '../components/RichTextLazy';
import { Badge, Button, Detail, Field, Input, Select, Table, Td, Textarea, Th, Tr, useToast, type BadgeTone } from '../components/ui';
import { useClientOptions } from './Invoices';
import { useCurrencies } from './Clients';
import { useContractValue } from './Contracts';

type Billing = 'MONTHLY' | 'YEARLY' | 'ONE_TIME';
type Basis = 'MONTH' | 'YEAR' | 'TOTAL';

interface ProjectOption {
  id: string;
  name: string;
  nameAr: string | null;
  code: string;
  status?: string;
  /** In support by hand, with no contract: this contract would take its support over. */
  supportByHand?: boolean;
}

/** A project still being built: a contract waits for it to be finished. */
const IN_PROGRESS = ['DRAFT', 'ACTIVE', 'TESTING', 'ON_HOLD'];

/** How each period of the preview reads: nothing is invoiced by itself (2026-10-27). */
const PREVIEW_TONE: Record<string, BadgeTone> = {
  INVOICED: 'success',
  DUE: 'warning',
  UPCOMING: 'info',
  SKIPPED: 'neutral',
  NOT_BILLED: 'neutral',
};

/** What saving brings a closed project back from, by its status. */
const REOPEN_HINT: Record<string, TranslationKey> = {
  COMPLETED: 'contracts.projectHintCompleted',
  CANCELLED: 'contracts.projectHintCancelled',
  ARCHIVED: 'contracts.projectHintArchived',
};

interface Preview {
  today: string;
  billFrom: string;
  total: number;
  periods: Array<{ start: string; end: string; months: number; amount: number; state: string }>;
}

/** A clause as the form holds it. `key` keeps its editor with it when clauses move. */
interface ClauseDraft {
  key: string;
  title: string;
  body: string;
}

interface FormState {
  clientId: string;
  projectId: string;
  title: string;
  preamble: string;
  clauses: ClauseDraft[];
  conclusion: string;
  ourSignatoryName: string;
  ourSignatoryPosition: string;
  clientSignatoryName: string;
  clientSignatoryPosition: string;
  startDate: string;
  endDate: string;
  value: string;
  valueBasis: Basis;
  billing: Billing;
  currency: string;
  paymentTermsDays: string;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** As many clauses as the API takes (modules/contracts/contract-html.ts, MAX_CLAUSES). */
const MAX_CLAUSES = 200;

let clauseCount = 0;
const clauseKey = () => `clause-${++clauseCount}`;

/** The day before the same date `years` later: a year from 1 Oct 2026 runs to 30 Sep 2027. */
function oneYearOn(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const last = new Date(Date.UTC(y + 1, m, 0)).getUTCDate();
  return addDays(new Date(Date.UTC(y + 1, m - 1, Math.min(d, last))).toISOString().slice(0, 10), -1);
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/**
 * New contract, and editing one. Client first, then its project (the picker
 * loads only that client's), the title, the dates, the payment value and what
 * it is for, the payment type, then the contract's text — its preamble, its
 * clauses and its conclusion (operator, 2026-10-09) — and who signs it for each
 * side. Below, every billing period the terms make — each invoiced by hand
 * from the contract's page (2026-10-27) — worked out by the API itself as the
 * fields change.
 *
 * Once the contract has an invoice its terms are fixed (the API refuses
 * them too): the page then changes only the title, the text, who signs it and,
 * while it runs, the payment terms.
 *
 * Routes: `/contracts/new` (optionally `?clientId=`) and `/contracts/:id/edit`.
 */
export function ContractFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const clients = useClientOptions();
  const currencies = useCurrencies();
  const valueOf = useContractValue();

  const existing = useQuery({
    queryKey: ['contract', id],
    queryFn: () => api.get<any>(`/contracts/${id}`),
    enabled: editing,
  });

  const today = localIsoDate();
  const [form, setForm] = useState<FormState>({
    clientId: searchParams.get('clientId') ?? '',
    projectId: searchParams.get('projectId') ?? '',
    title: '',
    preamble: '',
    clauses: [],
    conclusion: '',
    ourSignatoryName: '',
    ourSignatoryPosition: '',
    clientSignatoryName: '',
    clientSignatoryPosition: '',
    startDate: today,
    endDate: oneYearOn(today),
    value: '',
    valueBasis: 'MONTH',
    billing: 'MONTHLY',
    currency: '',
    paymentTermsDays: '30',
  });
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  // The clauses: added at the end, moved one place at a time, taken out. A new
  // one's title takes the focus, so the next thing typed is its title.
  const [focusClause, setFocusClause] = useState<string | null>(null);
  const setClause = (key: string, patch: Partial<ClauseDraft>) =>
    setForm((f) => ({ ...f, clauses: f.clauses.map((clause) => (clause.key === key ? { ...clause, ...patch } : clause)) }));
  const addClause = () => {
    const key = clauseKey();
    setFocusClause(key);
    setForm((f) => ({ ...f, clauses: [...f.clauses, { key, title: '', body: '' }] }));
  };
  const moveClause = (index: number, by: -1 | 1) =>
    setForm((f) => {
      const clauses = [...f.clauses];
      const [clause] = clauses.splice(index, 1);
      clauses.splice(index + by, 0, clause);
      return { ...f, clauses };
    });
  const removeClause = (key: string) => setForm((f) => ({ ...f, clauses: f.clauses.filter((clause) => clause.key !== key) }));

  // §15 — a new client brings its currency and clears the project. Tracked by
  // hand rather than in an effect, so the edit page loading its contract is
  // not taken for a change of client.
  const [lastClient, setLastClient] = useState(form.clientId);

  // The edit page starts from the contract as it is.
  const [seeded, setSeeded] = useState(false);
  if (editing && existing.data && !seeded) {
    const c = existing.data;
    setSeeded(true);
    setLastClient(c.client.id);
    setForm({
      clientId: c.client.id,
      projectId: c.project?.id ?? '',
      title: c.title,
      preamble: c.preamble ?? '',
      clauses: (c.clauses ?? []).map((clause: { title: string | null; body: string | null }) => ({
        key: clauseKey(),
        title: clause.title ?? '',
        body: clause.body ?? '',
      })),
      conclusion: c.conclusion ?? '',
      ourSignatoryName: c.ourSignatoryName ?? '',
      ourSignatoryPosition: c.ourSignatoryPosition ?? '',
      clientSignatoryName: c.clientSignatoryName ?? '',
      clientSignatoryPosition: c.clientSignatoryPosition ?? '',
      startDate: c.startDate,
      endDate: c.endDate,
      value: String(c.value),
      valueBasis: c.valueBasis,
      billing: c.billing,
      currency: c.currency,
      paymentTermsDays: String(c.paymentTermsDays),
    });
  }

  const termsLocked = editing && !!existing.data && !existing.data.can.editTerms;
  const paymentTermsLocked = editing && !!existing.data && !existing.data.can.editPaymentTerms;

  // The contract's own client and project stay choosable on its edit page even
  // when the pickers no longer offer them (a client switched off since).
  const clientOptions = [...(clients.data ?? [])];
  const savedClient = existing.data?.client;
  if (savedClient && !clientOptions.some((c) => c.id === savedClient.id)) clientOptions.unshift(savedClient);
  const client = clientOptions.find((c) => c.id === form.clientId);

  if (client && lastClient !== form.clientId) {
    setLastClient(form.clientId);
    setForm((f) => ({
      ...f,
      currency: client.defaultCurrency,
      projectId: searchParams.get('projectId') === f.projectId ? f.projectId : '',
    }));
  }
  useEffect(() => {
    if (!editing && client && !form.currency) set('currency', client.defaultCurrency);
  }, [client, editing, form.currency]);

  // Finished projects too (2026-10-09): a new contract brings a completed or
  // archived project back into support.
  const projects = useQuery({
    queryKey: ['project-options', form.clientId, 'contract'],
    queryFn: () => api.get<ProjectOption[]>('/projects/options', { clientId: form.clientId, forContract: true }),
    enabled: !!form.clientId,
  });
  const projectOptions = [...(projects.data ?? [])];
  const savedProject: ProjectOption | null = existing.data?.project ?? null;
  if (savedProject && savedProject.id === form.projectId && !projectOptions.some((p) => p.id === savedProject.id)) {
    projectOptions.unshift(savedProject);
  }

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, Record<string, unknown>>>('/settings'),
  });
  const mayChooseCurrency =
    Boolean(settings.data?.finance?.['finance.allowInvoiceCurrencyOverride']) || can(P.FINANCE_EXCHANGE_RATE_EDIT);
  const currencyRows = currencies.data ?? [];
  const baseCurrency = currencyRows.find((c) => c.isBase)?.code ?? '';
  const currencyRow = currencyRows.find((c) => c.code === form.currency);
  const noRate = !!form.currency && !!baseCurrency && form.currency !== baseCurrency && currencyRow?.rateToBase == null;

  // The preview: what the API would raise for these terms, once typing pauses.
  const value = Number(form.value);
  const termsValid =
    DATE.test(form.startDate) &&
    DATE.test(form.endDate) &&
    form.endDate >= form.startDate &&
    Number.isFinite(value) &&
    value > 0;
  const termsKey = JSON.stringify({
    startDate: form.startDate,
    endDate: form.endDate,
    value,
    valueBasis: form.valueBasis,
    billing: form.billing,
    ...(editing ? { contractId: id } : {}),
  });
  const settledKey = useDebounced(termsKey, 300);
  const preview = useQuery({
    queryKey: ['contract-preview', settledKey],
    queryFn: () => api.post<Preview>('/contracts/preview', JSON.parse(settledKey)),
    enabled: termsValid && settledKey === termsKey && !termsLocked,
    placeholderData: (previous) => previous,
    retry: false,
  });

  const paymentTerms = Number(form.paymentTermsDays);
  const valid =
    !!form.clientId &&
    !!form.title.trim() &&
    termsValid &&
    Number.isInteger(paymentTerms) &&
    paymentTerms >= 0 &&
    paymentTerms <= 365;

  // A clause with neither a title nor text is a slip, not a clause: it is left out.
  const clausesToSave = form.clauses
    .map((clause) => ({ title: clause.title.trim() || null, body: clause.body || null }))
    .filter((clause) => clause.title || clause.body);
  const emptyClauses = form.clauses.length - clausesToSave.length;
  // Blank, a field is left out of a new contract and cleared on an edit.
  const text = (value: string) => value.trim() || (editing ? null : undefined);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        title: form.title.trim(),
        preamble: form.preamble || (editing ? null : undefined),
        clauses: clausesToSave,
        conclusion: form.conclusion || (editing ? null : undefined),
        ourSignatoryName: text(form.ourSignatoryName),
        ourSignatoryPosition: text(form.ourSignatoryPosition),
        clientSignatoryName: text(form.clientSignatoryName),
        clientSignatoryPosition: text(form.clientSignatoryPosition),
        ...(paymentTermsLocked ? {} : { paymentTermsDays: paymentTerms }),
      };
      if (!termsLocked) {
        Object.assign(body, {
          clientId: form.clientId,
          projectId: form.projectId || (editing ? null : undefined),
          startDate: form.startDate,
          endDate: form.endDate,
          value,
          valueBasis: form.valueBasis,
          billing: form.billing,
          currency: form.currency || undefined,
        });
      }
      return editing ? api.patch<any>(`/contracts/${id}`, body) : api.post<any>('/contracts', body);
    },
    onSuccess: (contract) => {
      void queryClient.invalidateQueries({ queryKey: ['contracts'] });
      void queryClient.invalidateQueries({ queryKey: ['contract', contract.id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['client', contract.client.id] });
      void queryClient.invalidateQueries({ queryKey: ['client-invoices'] });
      // Nothing is invoiced on saving (2026-10-27): its periods wait on its page.
      toast.success(t(editing ? 'contracts.saved' : 'contracts.created'));
      navigate(`/contracts/${contract.id}`);
    },
    onError: (e: unknown) =>
      toast.error(
        e instanceof ApiError && e.code === 'CONTRACT_TERMS_LOCKED'
          ? t('contracts.termsLocked')
          : e instanceof Error
            ? e.message
            : t('common.error'),
      ),
  });

  const clientName = client ? (locale === 'ar' && client.companyNameAr ? client.companyNameAr : client.companyName) : null;

  // What the contract does to its project: when it starts, a project still
  // being built or completed goes into support and ends the day before
  // (2026-10-27); a cancelled or archived one comes back; one in support stays
  // (one in support by hand is then supported under the contract).
  const project = projectOptions.find((p) => p.id === form.projectId);
  const projectLabel = project ? `${project.code} — ${localized(project)}` : '';
  const projectHint = !form.clientId
    ? t('invoices.pickClientFirst')
    : !project?.status
      ? t('contracts.projectHint')
      : project.status === 'SUPPORT'
        ? t(project.supportByHand ? 'contracts.projectHintSupportByHand' : 'contracts.projectHintSupport', { project: projectLabel })
        : IN_PROGRESS.includes(project.status)
          ? t('contracts.projectHintOpen', { project: projectLabel })
          : t(REOPEN_HINT[project.status] ?? 'contracts.projectHintCompleted', { project: projectLabel });
  const billable = (preview.data?.periods ?? []).filter((p) => p.state !== 'SKIPPED' && p.state !== 'NOT_BILLED');
  const billableTotal = billable.reduce((sum, p) => sum + p.amount, 0);
  const currencyForMoney = form.currency || client?.defaultCurrency || baseCurrency || 'EGP';

  return (
    <FormPage
      title={editing ? t('contracts.edit') : t('contracts.new')}
      subtitle={editing && existing.data ? `${existing.data.number} — ${existing.data.title}` : undefined}
      backTo={editing ? `/contracts/${id}` : '/contracts'}
      backLabel={editing && existing.data ? existing.data.number : t('contracts.title')}
      width="lg"
      submitLabel={editing ? t('common.save') : t('common.create')}
      submitting={save.isPending}
      disabled={!valid}
      loading={editing && existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        {termsLocked && (
          <p className="rounded-md border border-warning/40 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark sm:col-span-2">
            {existing.data?.status === 'ACTIVE' ? t('contracts.termsLocked') : t('contracts.endedLocked')}
          </p>
        )}

        <Field label={t('invoices.client')} required>
          <Select
            value={form.clientId}
            onChange={(e) => set('clientId', e.target.value)}
            disabled={termsLocked}
            autoFocus={!editing}
          >
            <option value="">—</option>
            {clientOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {locale === 'ar' && c.companyNameAr ? c.companyNameAr : c.companyName}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('invoices.project')} hint={projectHint}>
          <Select
            value={form.projectId}
            onChange={(e) => set('projectId', e.target.value)}
            disabled={!form.clientId || termsLocked}
          >
            <option value="">{t('invoices.noProject')}</option>
            {projectOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} — {localized(p)}
                {p.status && !IN_PROGRESS.includes(p.status) ? ` (${projectStatusLabel(p.status, locale)})` : ''}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('contracts.contractTitle')} required className="sm:col-span-2" hint={t('contracts.titleHint')}>
          <Input value={form.title} maxLength={200} onChange={(e) => set('title', e.target.value)} />
        </Field>

        <Field label={t('contracts.startDate')} required>
          <Input
            type="date"
            value={form.startDate}
            disabled={termsLocked}
            onChange={(e) => {
              const start = e.target.value;
              // Moving the start keeps a contract that ran a year running a year.
              setForm((f) => ({
                ...f,
                startDate: start,
                endDate: DATE.test(start) && f.endDate === oneYearOn(f.startDate) ? oneYearOn(start) : f.endDate,
              }));
            }}
          />
        </Field>
        <Field
          label={t('contracts.endDate')}
          required
          hint={t('contracts.endDateHint')}
          error={DATE.test(form.endDate) && form.endDate < form.startDate ? t('contracts.endBeforeStart') : null}
        >
          <Input type="date" value={form.endDate} disabled={termsLocked} onChange={(e) => set('endDate', e.target.value)} />
        </Field>

        <Field label={t('contracts.value')} required>
          <Input
            type="number"
            step="0.01"
            min="0"
            dir="ltr"
            value={form.value}
            disabled={termsLocked}
            onChange={(e) => set('value', e.target.value)}
          />
        </Field>
        <Field label={t('contracts.valueBasis')} required>
          <Select value={form.valueBasis} disabled={termsLocked} onChange={(e) => set('valueBasis', e.target.value as Basis)}>
            {(['MONTH', 'YEAR', 'TOTAL'] as const).map((b) => (
              <option key={b} value={b}>{t(`contracts.basis.${b}`)}</option>
            ))}
          </Select>
        </Field>

        <Field label={t('contracts.billing')} required hint={t(`contracts.billingHint.${form.billing}` as TranslationKey)}>
          <Select value={form.billing} disabled={termsLocked} onChange={(e) => set('billing', e.target.value as Billing)}>
            {(['MONTHLY', 'YEARLY', 'ONE_TIME'] as const).map((b) => (
              <option key={b} value={b}>{t(`contracts.billing.${b}`)}</option>
            ))}
          </Select>
        </Field>

        <Field
          label={t('common.currency')}
          hint={
            noRate
              ? t('contracts.noRate', { code: form.currency, base: baseCurrency })
              : client && form.currency && form.currency !== client.defaultCurrency
                ? t('invoices.currencyDiffers', { currency: client.defaultCurrency })
                : undefined
          }
        >
          {mayChooseCurrency && !termsLocked ? (
            <Select value={form.currency} onChange={(e) => set('currency', e.target.value)} dir="ltr">
              {!form.currency && <option value="">—</option>}
              {currencyRows.map((c) => (
                <option key={c.code} value={c.code}>{c.code} — {localized(c)}</option>
              ))}
            </Select>
          ) : (
            <Input value={form.currency} readOnly disabled dir="ltr" />
          )}
        </Field>

        <Field label={t('contracts.paymentTerms')} required hint={t('contracts.paymentTermsHint')}>
          <Input
            type="number"
            step="1"
            min="0"
            max="365"
            dir="ltr"
            value={form.paymentTermsDays}
            disabled={paymentTermsLocked}
            onChange={(e) => set('paymentTermsDays', e.target.value)}
          />
        </Field>

        <Section title={t('contracts.wording')} hint={t('contracts.wordingHint')}>
          <RichField label={t('contracts.preamble')} hint={t('contracts.preambleHint')}>
            <RichTextEditor value={form.preamble} onChange={(html) => set('preamble', html)} label={t('contracts.preamble')} />
          </RichField>

          <div>
            <p className="text-body-sm font-medium text-[var(--text-secondary)]">{t('contracts.clauses')}</p>
            <p className="mt-0.5 text-caption text-faint">{t('contracts.clausesHint')}</p>
            {form.clauses.length > 0 && (
              <ol className="mt-3 grid gap-3">
                {form.clauses.map((clause, index) => {
                  const n = index + 1;
                  return (
                    <li key={clause.key} className="rounded-lg border hairline p-4">
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <span className="text-overline uppercase text-primary-600 dark:text-primary-400">{t('contracts.clause', { n })}</span>
                        <span className="flex items-center gap-1">
                          <ClauseButton
                            label={t('contracts.moveClauseUp', { n })}
                            disabled={index === 0}
                            onClick={() => moveClause(index, -1)}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </ClauseButton>
                          <ClauseButton
                            label={t('contracts.moveClauseDown', { n })}
                            disabled={index === form.clauses.length - 1}
                            onClick={() => moveClause(index, 1)}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </ClauseButton>
                          <ClauseButton label={t('contracts.removeClause', { n })} onClick={() => removeClause(clause.key)}>
                            <Trash2 className="h-4 w-4" />
                          </ClauseButton>
                        </span>
                      </div>
                      <Field label={t('contracts.clauseTitle')}>
                        <Input
                          value={clause.title}
                          maxLength={200}
                          dir="auto"
                          autoFocus={clause.key === focusClause}
                          onChange={(e) => setClause(clause.key, { title: e.target.value })}
                        />
                      </Field>
                      <div className="mt-3">
                        <p className="mb-1.5 text-body-sm font-medium text-[var(--text-secondary)]">{t('contracts.clauseText')}</p>
                        <RichTextEditor
                          compact
                          value={clause.body}
                          onChange={(html) => setClause(clause.key, { body: html })}
                          label={`${t('contracts.clause', { n })} — ${t('contracts.clauseText')}`}
                        />
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button icon={<Plus className="h-4 w-4" />} onClick={addClause} disabled={form.clauses.length >= MAX_CLAUSES}>
                {t('contracts.addClause')}
              </Button>
              {emptyClauses > 0 && <span className="text-caption text-warning-fg dark:text-warning-dark">{t('contracts.emptyClausesDropped')}</span>}
            </div>
          </div>

          <RichField label={t('contracts.conclusion')} hint={t('contracts.conclusionHint')}>
            <RichTextEditor value={form.conclusion} onChange={(html) => set('conclusion', html)} label={t('contracts.conclusion')} />
          </RichField>
        </Section>

        <Section title={t('contracts.signatures')} hint={t('contracts.signaturesHint')}>
          <div className="grid gap-4 sm:grid-cols-2">
            <fieldset className="grid content-start gap-3 rounded-lg border hairline p-4">
              <legend className="px-1 text-body-sm font-medium text-[var(--text-primary)]">{t('contracts.ourSide')}</legend>
              <Field label={t('contracts.personInCharge')}>
                <Input
                  value={form.ourSignatoryName}
                  maxLength={200}
                  dir="auto"
                  onChange={(e) => set('ourSignatoryName', e.target.value)}
                />
              </Field>
              <Field label={t('contracts.positionOfPerson')}>
                <Input
                  value={form.ourSignatoryPosition}
                  maxLength={200}
                  dir="auto"
                  onChange={(e) => set('ourSignatoryPosition', e.target.value)}
                />
              </Field>
            </fieldset>
            <fieldset className="grid content-start gap-3 rounded-lg border hairline p-4">
              <legend className="px-1 text-body-sm font-medium text-[var(--text-primary)]">
                {clientName ? t('contracts.clientSide', { client: clientName }) : t('contracts.clientSideNone')}
              </legend>
              <Field label={t('contracts.personInCharge')}>
                <Input
                  value={form.clientSignatoryName}
                  maxLength={200}
                  dir="auto"
                  onChange={(e) => set('clientSignatoryName', e.target.value)}
                />
              </Field>
              <Field label={t('contracts.positionOfPerson')}>
                <Input
                  value={form.clientSignatoryPosition}
                  maxLength={200}
                  dir="auto"
                  onChange={(e) => set('clientSignatoryPosition', e.target.value)}
                />
              </Field>
            </fieldset>
          </div>
        </Section>

        {!termsLocked && (
          <div className="rounded-lg border hairline sm:col-span-2" aria-live="polite">
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b hairline px-4 py-3">
              <p className="text-title text-[var(--text-primary)]">{t('contracts.previewTitle')}</p>
              {termsValid && preview.data && (
                <p className="text-body-sm text-muted">
                  {valueOf({ value, valueBasis: form.valueBasis, currency: currencyForMoney })}
                </p>
              )}
            </div>
            {!termsValid ? (
              <p className="px-4 py-4 text-body-sm text-muted">{t('contracts.previewEmpty')}</p>
            ) : preview.error ? (
              <p className="px-4 py-4 text-body-sm text-error-fg dark:text-error-dark">
                {preview.error instanceof Error ? preview.error.message : t('common.error')}
              </p>
            ) : !preview.data ? (
              <p className="px-4 py-4 text-body-sm text-muted">{t('common.loading')}</p>
            ) : (
              <>
                <div className="max-h-80 overflow-y-auto">
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('contracts.period')}</Th>
                        <Th>{t('common.status')}</Th>
                        <Th align="end">{t('common.amount')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.data.periods.map((p) => (
                        <Tr key={p.start} className={p.state === 'SKIPPED' || p.state === 'NOT_BILLED' ? 'opacity-60' : undefined}>
                          <Td className="tabular sm:whitespace-nowrap">
                            {formatDate(p.start, locale)} – {formatDate(p.end, locale)}
                          </Td>
                          <Td className="whitespace-nowrap">
                            {/* Nothing is invoiced by itself: a begun period waits for someone to invoice it. */}
                            <Badge tone={PREVIEW_TONE[p.state] ?? 'neutral'}>
                              {t(`contracts.state.${p.state}` as TranslationKey)}
                            </Badge>
                          </Td>
                          <Td align="end" className="whitespace-nowrap tabular">
                            {formatMoney(p.amount, currencyForMoney, locale)}
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <dl className="divide-y divide-[var(--line)] border-t hairline px-4 py-2">
                  <Detail label={t('contracts.previewInvoices')}>
                    <span className="tabular">{billable.length}</span>
                  </Detail>
                  <Detail label={t('contracts.previewTotal')}>
                    <strong className="tabular">{formatMoney(billableTotal, currencyForMoney, locale)}</strong>
                  </Detail>
                </dl>
                <p className="border-t hairline px-4 py-3 text-caption text-muted">
                  {t('contracts.issuedAtOnce')}
                  {preview.data.periods.some((p) => p.state === 'SKIPPED') && <> {t('contracts.skippedHint')}</>}
                </p>
              </>
            )}
          </div>
        )}
      </FormGrid>
    </FormPage>
  );
}

/** A part of the form under its own heading, across both columns, ruled off from what comes before. */
function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="border-t hairline pt-5 sm:col-span-2">
      <h2 className="text-title text-[var(--text-primary)]">{title}</h2>
      {hint && <p className="mt-0.5 text-body-sm text-muted">{hint}</p>}
      <div className="mt-4 grid gap-5">{children}</div>
    </section>
  );
}

/**
 * A rich-text field. Not a `Field`: that is a <label>, and a label around an
 * editor sends a click on the toolbar to the editor's first line.
 */
function RichField({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-body-sm font-medium text-[var(--text-secondary)]">{label}</p>
      {children}
      {hint && <p className="mt-1 text-caption text-faint">{hint}</p>}
    </div>
  );
}

/** Move up, move down, remove: a clause's own small buttons, named for whoever cannot see the icon. */
function ClauseButton({
  label,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button size="sm" variant="ghost" className="w-8 px-0" title={label} aria-label={label} disabled={disabled} onClick={onClick}>
      {children}
    </Button>
  );
}

/**
 * Ending a contract early takes a reason, so it is a page (the console's rule
 * for any confirmation with typed input). Route: `/contracts/:id/terminate`.
 */
export function ContractTerminatePage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const valueOf = useContractValue();
  const [reason, setReason] = useState('');

  const contract = useQuery({
    queryKey: ['contract', id],
    queryFn: () => api.get<any>(`/contracts/${id}`),
  });

  const end = useMutation({
    mutationFn: () => api.post(`/contracts/${id}/terminate`, { reason: reason.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contract', id] });
      void queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success(t('contracts.ended'));
      navigate(`/contracts/${id}`);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });

  const c = contract.data;
  return (
    <FormPage
      title={t('contracts.terminateTitle')}
      backTo={`/contracts/${id}`}
      backLabel={c?.number ?? t('contracts.title')}
      submitLabel={t('contracts.terminate')}
      destructive
      submitting={end.isPending}
      disabled={!reason.trim()}
      loading={contract.isLoading}
      error={contract.error}
      onRetry={() => void contract.refetch()}
      onSubmit={() => end.mutate()}
      summary={
        c && (
          <dl className="grid gap-x-6 sm:grid-cols-2">
            <Detail label={t('contracts.number')}>
              <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{c.number}</span>
            </Detail>
            <Detail label={t('invoices.client')}>
              {locale === 'ar' && c.client.companyNameAr ? c.client.companyNameAr : c.client.companyName}
            </Detail>
            <Detail label={t('contracts.value')}>{valueOf(c)}</Detail>
            <Detail label={t('contracts.summary.invoiced')}>{formatMoney(c.summary.invoiced, c.currency, locale)}</Detail>
            <Detail label={t('contracts.summary.outstanding')}>{formatMoney(c.summary.outstanding, c.currency, locale)}</Detail>
            <Detail label={t('contracts.summary.notInvoiced')}>{formatMoney(c.summary.notInvoiced, c.currency, locale)}</Detail>
          </dl>
        )
      }
    >
      <p className="mb-4 text-body-sm text-muted">{t('contracts.terminateNote')}</p>
      <Field label={t('contracts.terminateReason')} required>
        <Textarea value={reason} maxLength={2000} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </FormPage>
  );
}
